
import "./TestStyles.css";
import { handleOnPopState } from '../utils/pop_state_utils.js';
import React, { useState, useEffect, useCallback } from "react";
import { UpdateLocalStorage } from '../db/localstorage_utils.js';
import { Link, useNavigate, useLocation } from "react-router-dom";
import { playInstructionsOnce, handleAudioEndedCommon } from '../utils/audio_utils.js';

import { calculate_progress } from "../utils/progress_bar.js";
 
import DropDownComponent from "./DropDownComponent";

const QuestionTwoScreen = ({ userData }) => {
  const year = new Date().getFullYear();
  const [audio] = useState(new Audio("/audio/q2_instructions.mp3"));
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [nextButtonStyle, setNextButtonStyle] = useState("NextButton");
  const [audioPlayed, setAudioPlayed] = useState(false);
  const [ipAddress, setIPAddress] = useState("");
  const [yearDropdown, setYearDropdown] = useState([]);
  const [dateDropdown, setDateDropdown] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const results = JSON.parse(localStorage.getItem("results"));
  const [nextPage] = useState("/Q19Screen");        // Where this screen will navigate to next
  const [thisPage] = useState("screen_2");  
  const handleAudioEnded = useCallback(() => {
    handleAudioEndedCommon({
      audio,
      onEnded: handleAudioEnded,
      setAudioPlayed,
      setButtonDisabled,
      setNextButtonStyle
    });
  }, [audio]);

   
  // Implementing the popstate handler
  window.onpopstate = () => {
    handleOnPopState({ results, userData, navigate, location });
  };

/**
 * The function `updateDateDropDowns` creates dropdown options for selecting years and dates.
 */
  const updateDateDropDowns = useCallback(async () => {
    let newYearDropdown = [];
    for (let i = 2000; i <= year+5; i++) {
      let newYear = { value: i, label: i };
      newYearDropdown.push(newYear);
    }
    let newDateDropdown = [];
    for (let i = 1; i <= 31; i++) {
      let newDate = { value: i, label: i };
      newDateDropdown.push(newDate);
    }
    await setYearDropdown(newYearDropdown);
    await setDateDropdown(newDateDropdown);
  }, [year]);

  /* The `useEffect` hook makes sure that the instruction audio is played only once.
  * The user's IP address is set using this hook, the next button is enabled after the instructions are complete. 
  */
  useEffect(() => {
    updateDateDropDowns();
    fetch("https://api.ipify.org?format=json")
      .then((response) => response.json())
      .then((data) => setIPAddress(data.ip))
      .catch((error) => console.log(error));
    if (!audioPlayed) {
      playInstructionsOnce({ audio, audioPlayed, setAudioPlayed, onEnded: handleAudioEnded });
    }
  }, [audio, audioPlayed, updateDateDropDowns, handleAudioEnded]);

  /**
   * The handleChange function stores the value of an input field in the browser's local storage.
   * @param event - The event parameter is an object that contains information about the event that
   * occurred, such as the type of event, the target element that triggered the event, and any
   * additional data related to the event. In this case, the event parameter is used to access the
   * target element's className and value.
   */
  const handleChange = (event) => {
    localStorage.setItem(event.target.className, event.target.value);
  };

  const weekDayDropdown = [
    { value: "Monday", label: "Monday" },
    { value: "Tuesday", label: "Tuesday" },
    { value: "Wednesday", label: "Wednesday" },
    { value: "Thursday", label: "Thursday" },
    { value: "Friday", label: "Friday" },
    { value: "Saturday", label: "Saturday" },
    { value: "Sunday", label: "Sunday" },
  ];

  const monthDropdown = [
    { value: "January", label: "January" },
    { value: "February", label: "February" },
    { value: "March", label: "March" },
    { value: "April", label: "April" },
    { value: "May", label: "May" },
    { value: "June", label: "June" },
    { value: "July", label: "July" },
    { value: "August", label: "August" },
    { value: "September", label: "September" },
    { value: "October", label: "October" },
    { value: "November", label: "November" },
    { value: "December", label: "December" },
  ];

  const seasonDropdown = [
    { value: "Spring", label: "Spring" },
    { value: "Summer", label: "Summer" },
    { value: "Autumn", label: "Autumn" },
    { value: "Winter", label: "Winter" },
  ];

  const testMethodDropdown = [
    { value: "Paper", label: "Paper" },
    { value: "Program", label: "Program" },
    { value: "Conversation", label: "Conversation" },
    { value: "Doctor", label: "Doctor" },
  ];

  const countryDropdown = [
    { value: "GB", label: "United kingdom" },
    { value: "US", label: "United states" },
    { value: "CA", label: "Canada" },
    { value: "AF", label: "Afghanistan" },
    { value: "AL", label: "Albania" },
    { value: "DZ", label: "Algeria" },
    { value: "AS", label: "American samoa" },
    { value: "AD", label: "Andorra" },
    { value: "AO", label: "Angola" },
    { value: "AI", label: "Anguilla" },
    { value: "AQ", label: "Antarctica" },
    { value: "AG", label: "Antigua and barbuda" },
    { value: "AR", label: "Argentina" },
    { value: "AM", label: "Armenia" },
    { value: "AW", label: "Aruba" },
    { value: "AU", label: "Australia" },
    { value: "AT", label: "Austria" },
    { value: "AZ", label: "Azerbaijan" },
    { value: "BS", label: "Bahamas" },
    { value: "BH", label: "Bahrain" },
    { value: "BD", label: "Bangladesh" },
    { value: "BB", label: "Barbados" },
    { value: "BY", label: "Belarus" },
    { value: "BE", label: "Belgium" },
    { value: "BZ", label: "Belize" },
    { value: "BJ", label: "Benin" },
    { value: "BM", label: "Bermuda" },
    { value: "BT", label: "Bhutan" },
    { value: "BO", label: "Bolivia, plurinational state of" },
    { value: "BQ", label: "Bonaire, sint eustatius and saba" },
    { value: "BA", label: "Bosnia and herzegovina" },
    { value: "BW", label: "Botswana" },
    { value: "BV", label: "Bouvet island" },
    { value: "BR", label: "Brazil" },
    { value: "IO", label: "British indian ocean territory" },
    { value: "BN", label: "Brunei darussalam" },
    { value: "BG", label: "Bulgaria" },
    { value: "BF", label: "Burkina faso" },
    { value: "BI", label: "Burundi" },
    { value: "KH", label: "Cambodia" },
    { value: "CM", label: "Cameroon" },
    { value: "CV", label: "Cape verde" },
    { value: "KY", label: "Cayman islands" },
    { value: "CF", label: "Central african republic" },
    { value: "TD", label: "Chad" },
    { value: "CL", label: "Chile" },
    { value: "CN", label: "China" },
    { value: "CX", label: "Christmas island" },
    { value: "CC", label: "Cocos (keeling) islands" },
    { value: "CO", label: "Colombia" },
    { value: "KM", label: "Comoros" },
    { value: "CG", label: "Congo" },
    { value: "CD", label: "Congo, the democratic republic of the" },
    { value: "CK", label: "Cook islands" },
    { value: "CR", label: "Costa rica" },
    { value: "HR", label: "Croatia" },
    { value: "CU", label: "Cuba" },
    { value: "CY", label: "Cyprus" },
    { value: "CZ", label: "Czech republic" },
    { value: "DK", label: "Denmark" },
    { value: "DJ", label: "Djibouti" },
    { value: "DM", label: "Dominica" },
    { value: "DO", label: "Dominican republic" },
    { value: "EC", label: "Ecuador" },
    { value: "EG", label: "Egypt" },
    { value: "SV", label: "El salvador" },
    { value: "GQ", label: "Equatorial guinea" },
    { value: "ER", label: "Eritrea" },
    { value: "EE", label: "Estonia" },
    { value: "ET", label: "Ethiopia" },
    { value: "FK", label: "Falkland islands (malvinas)" },
    { value: "FO", label: "Faroe islands" },
    { value: "FJ", label: "Fiji" },
    { value: "FI", label: "Finland" },
    { value: "FR", label: "France" },
    { value: "GF", label: "French guiana" },
    { value: "PF", label: "French polynesia" },
    { value: "TF", label: "French southern territories" },
    { value: "GA", label: "Gabon" },
    { value: "GM", label: "Gambia" },
    { value: "GE", label: "Georgia" },
    { value: "DE", label: "Germany" },
    { value: "GH", label: "Ghana" },
    { value: "GI", label: "Gibraltar" },
    { value: "GR", label: "Greece" },
    { value: "GL", label: "Greenland" },
    { value: "GD", label: "Grenada" },
    { value: "GP", label: "Guadeloupe" },
    { value: "GU", label: "Guam" },
    { value: "GT", label: "Guatemala" },
    { value: "GG", label: "Guernsey" },
    { value: "GN", label: "Guinea" },
    { value: "GW", label: "Guinea-bissau" },
    { value: "GY", label: "Guyana" },
    { value: "HT", label: "Haiti" },
    { value: "HM", label: "Heard island and mcdonald islands" },
    { value: "VA", label: "Holy see (vatican city state)" },
    { value: "HN", label: "Honduras" },
    { value: "HK", label: "Hong kong" },
    { value: "HU", label: "Hungary" },
    { value: "IS", label: "Iceland" },
    { value: "IN", label: "India" },
    { value: "ID", label: "Indonesia" },
    { value: "IR", label: "Iran, islamic republic of" },
    { value: "IQ", label: "Iraq" },
    { value: "IE", label: "Ireland" },
    { value: "IM", label: "Isle of man" },
    { value: "IL", label: "Israel" },
    { value: "IT", label: "Italy" },
    { value: "JM", label: "Jamaica" },
    { value: "JP", label: "Japan" },
    { value: "JE", label: "Jersey" },
    { value: "JO", label: "Jordan" },
    { value: "KZ", label: "Kazakhstan" },
    { value: "KE", label: "Kenya" },
    { value: "KI", label: "Kiribati" },
    { value: "KP", label: "Korea, democratic people's republic of" },
    { value: "KR", label: "Korea, republic of" },
    { value: "KW", label: "Kuwait" },
    { value: "KG", label: "Kyrgyzstan" },
    { value: "LA", label: "Lao people's democratic republic" },
    { value: "LV", label: "Latvia" },
    { value: "LB", label: "Lebanon" },
    { value: "LS", label: "Lesotho" },
    { value: "LR", label: "Liberia" },
    { value: "LY", label: "Libyan arab jamahiriya" },
    { value: "LI", label: "Liechtenstein" },
    { value: "LT", label: "Lithuania" },
    { value: "LU", label: "Luxembourg" },
    { value: "MO", label: "Macao" },
    { value: "MK", label: "Macedonia, the former yugoslav republic of" },
    { value: "MG", label: "Madagascar" },
    { value: "MW", label: "Malawi" },
    { value: "MY", label: "Malaysia" },
    { value: "MV", label: "Maldives" },
    { value: "ML", label: "Mali" },
    { value: "MT", label: "Malta" },
    { value: "MH", label: "Marshall islands" },
    { value: "MQ", label: "Martinique" },
    { value: "MR", label: "Mauritania" },
    { value: "MU", label: "Mauritius" },
    { value: "YT", label: "Mayotte" },
    { value: "MX", label: "Mexico" },
    { value: "FM", label: "Micronesia, federated states of" },
    { value: "MD", label: "Moldova, republic of" },
    { value: "MC", label: "Monaco" },
    { value: "MN", label: "Mongolia" },
    { value: "ME", label: "Montenegro" },
    { value: "MS", label: "Montserrat" },
    { value: "MA", label: "Morocco" },
    { value: "MZ", label: "Mozambique" },
    { value: "MM", label: "Myanmar" },
    { value: "NA", label: "Namibia" },
    { value: "NR", label: "Nauru" },
    { value: "NP", label: "Nepal" },
    { value: "NL", label: "Netherlands" },
    { value: "NC", label: "New caledonia" },
    { value: "NZ", label: "New zealand" },
    { value: "NI", label: "Nicaragua" },
    { value: "NE", label: "Niger" },
    { value: "NG", label: "Nigeria" },
    { value: "NU", label: "Niue" },
    { value: "NF", label: "Norfolk island" },
    { value: "MP", label: "Northern mariana islands" },
    { value: "NO", label: "Norway" },
    { value: "OM", label: "Oman" },
    { value: "PK", label: "Pakistan" },
    { value: "PW", label: "Palau" },
    { value: "PS", label: "Palestinian territory, occupied" },
    { value: "PA", label: "Panama" },
    { value: "PG", label: "Papua new guinea" },
    { value: "PY", label: "Paraguay" },
    { value: "PE", label: "Peru" },
    { value: "PH", label: "Philippines" },
    { value: "PN", label: "Pitcairn" },
    { value: "PL", label: "Poland" },
    { value: "PT", label: "Portugal" },
    { value: "PR", label: "Puerto rico" },
    { value: "QA", label: "Qatar" },
    { value: "RO", label: "Romania" },
    { value: "RU", label: "Russian federation" },
    { value: "RW", label: "Rwanda" },
    { value: "SH", label: "Saint helena, ascension and tristan da cunha" },
    { value: "KN", label: "Saint kitts and nevis" },
    { value: "LC", label: "Saint lucia" },
    { value: "MF", label: "Saint martin (french part)" },
    { value: "PM", label: "Saint pierre and miquelon" },
    { value: "VC", label: "Saint vincent and the grenadines" },
    { value: "WS", label: "Samoa" },
    { value: "SM", label: "San marino" },
    { value: "ST", label: "Sao tome and principe" },
    { value: "SA", label: "Saudi arabia" },
    { value: "SN", label: "Senegal" },
    { value: "RS", label: "Serbia" },
    { value: "SC", label: "Seychelles" },
    { value: "SL", label: "Sierra leone" },
    { value: "SG", label: "Singapore" },
    { value: "SX", label: "Sint maarten (dutch part)" },
    { value: "SK", label: "Slovakia" },
    { value: "SI", label: "Slovenia" },
    { value: "SB", label: "Solomon islands" },
    { value: "SO", label: "Somalia" },
    { value: "ZA", label: "South africa" },
    { value: "GS", label: "South georgia and the south sandwich islands" },
    { value: "SS", label: "South sudan" },
    { value: "ES", label: "Spain" },
    { value: "LK", label: "Sri lanka" },
    { value: "SD", label: "Sudan" },
    { value: "SR", label: "Suriname" },
    { value: "SJ", label: "Svalbard and jan mayen" },
    { value: "SZ", label: "Swaziland" },
    { value: "SE", label: "Sweden" },
    { value: "CH", label: "Switzerland" },
    { value: "SY", label: "Syrian arab republic" },
    { value: "TW", label: "Taiwan, province of china" },
    { value: "TJ", label: "Tajikistan" },
    { value: "TZ", label: "Tanzania, united republic of" },
    { value: "TH", label: "Thailand" },
    { value: "TL", label: "Timor-leste" },
    { value: "TG", label: "Togo" },
    { value: "TK", label: "Tokelau" },
    { value: "TO", label: "Tonga" },
    { value: "TT", label: "Trinidad and tobago" },
    { value: "TN", label: "Tunisia" },
    { value: "TR", label: "Turkey" },
    { value: "TM", label: "Turkmenistan" },
    { value: "TC", label: "Turks and caicos islands" },
    { value: "TV", label: "Tuvalu" },
    { value: "UG", label: "Uganda" },
    { value: "UA", label: "Ukraine" },
    { value: "AE", label: "United arab emirates" },
    { value: "UM", label: "United states minor outlying islands" },
    { value: "UY", label: "Uruguay" },
    { value: "UZ", label: "Uzbekistan" },
    { value: "VU", label: "Vanuatu" },
    { value: "VE", label: "Venezuela, bolivarian republic of" },
    { value: "VN", label: "Viet nam" },
    { value: "VG", label: "Virgin islands, british" },
    { value: "VI", label: "Virgin islands, u.s." },
    { value: "WF", label: "Wallis and futuna" },
    { value: "EH", label: "Western sahara" },
    { value: "YE", label: "Yemen" },
    { value: "ZM", label: "Zambia" },
    { value: "ZW", label: "Zimbabwe" },
  ];

  const continentDropdown = [
    { value: "Africa", label: "Africa" },
    { value: "Antarctica", label: "Antarctica" },
    { value: "Australia", label: "Australia" },
    { value: "Asia", label: "Asia" },
    { value: "Europe", label: "Europe" },
    { value: "North America", label: "North America" },
    { value: "South America", label: "South America" },
  ];

  /**
   * The function handleLocalStorage stores a JSON object containing various data in the local storage.
   */
  const handleLocalStorage = async () => {
    const screenData = {
      ip_address: ipAddress,
      week_day: localStorage.getItem("weekDay-dropdown"),
      month: localStorage.getItem("month-dropdown"),
      year: localStorage.getItem("year-dropdown"),
      date: localStorage.getItem("date-dropdown"),
      season: localStorage.getItem("season-dropdown"),
      test_method: localStorage.getItem("testMethod-dropdown"),
      city_town_name: localStorage.getItem("cityTownInput"),
      province_state_name: localStorage.getItem("provinceStateInput"),
      country: localStorage.getItem("country-dropdown"),
      continent_name: localStorage.getItem("continent-dropdown"),
    };

    try { UpdateLocalStorage(screenData, thisPage); } catch (error) {}
  };

  return (
    <div className="Container">
      <div className="InstructionsContainer">
        <img className="CharacterIcon" src="/icons/other.png" alt="Character Icon" />
        <progress variant="custom" className="ProgressBar" value={calculate_progress('asymptotic', 51, 54)} max="1"></progress>
        <DropDownComponent placeholder="Day of the week" options={weekDayDropdown} className="weekDay-dropdown" />
        <DropDownComponent placeholder="Month of the year" options={monthDropdown} className="month-dropdown" />
        <DropDownComponent placeholder="Current year" options={yearDropdown} className="year-dropdown" />
        <DropDownComponent placeholder="What is the date?" options={dateDropdown} className="date-dropdown" />
        <DropDownComponent placeholder="Current season" options={seasonDropdown} className="season-dropdown" />
        <DropDownComponent
          placeholder="How are you doing this test"
          options={testMethodDropdown}
          className="testMethod-dropdown"
        />
        <input placeholder="City or town name" className="cityTownInput" onChange={handleChange} />
        <input placeholder="Province or state name" className="provinceStateInput" onChange={handleChange} />
        <DropDownComponent placeholder="Country name?" options={countryDropdown} className="country-dropdown" />
        <DropDownComponent placeholder="Continent name" options={continentDropdown} className="continent-dropdown" />
      </div>
      <div className="NextButtonContainer">
        <Link to={nextPage}>
          <button disabled={buttonDisabled} onClick={handleLocalStorage} className={nextButtonStyle}>
            <img src="/icons/arrow.png" alt="Next Button" />
          </button>
        </Link>
      </div>
    </div>
  );
};

export default QuestionTwoScreen;
